import type { Components } from 'react-markdown';

/**
 * Create markdown rendering components with optional .md link handler.
 * When onNavigate is provided, links ending in .md become clickable
 * and navigate within the docs viewer instead of opening externally.
 */
export function createMarkdownComponents(
  onNavigate?: (path: string) => void,
  currentFilePath?: string,
): Components {
  return {
    h1: ({ children }) => (
      <h1 className="text-xl font-bold text-text-primary mt-6 mb-3">{children}</h1>
    ),
    h2: ({ children }) => (
      <h2 className="text-base font-semibold text-accent-purple-light mt-8 mb-3 font-mono border-t border-border-subtle pt-4 first:mt-0 first:border-t-0 first:pt-0">
        {children}
      </h2>
    ),
    h3: ({ children }) => {
      const text = String(children);
      let borderClass = 'border-text-muted';
      if (text.includes('Decision')) borderClass = 'border-info';
      else if (text.includes('Preference')) borderClass = 'border-success';
      else if (text.includes('Pattern')) borderClass = 'border-warning';
      else if (text.includes('Fact')) borderClass = 'border-accent-purple';
      return (
        <h3 className={`text-sm font-semibold text-text-primary mt-4 mb-2 border-l-2 ${borderClass} pl-3`}>
          {children}
        </h3>
      );
    },
    p: ({ children }) => (
      <p className="text-sm text-text-secondary leading-relaxed mb-2">{children}</p>
    ),
    ul: ({ children }) => (
      <ul className="text-sm text-text-secondary ml-4 list-disc mb-3 space-y-1">{children}</ul>
    ),
    ol: ({ children }) => (
      <ol className="text-sm text-text-secondary ml-4 list-decimal mb-3 space-y-1">{children}</ol>
    ),
    li: ({ children }) => (
      <li className="text-sm text-text-secondary leading-relaxed">{children}</li>
    ),
    code: ({ className, children }) => {
      const isBlock = className?.includes('language-');
      if (isBlock) {
        return <code className={`text-xs font-mono ${className || ''}`}>{children}</code>;
      }
      return (
        <code className="bg-bg-tertiary text-accent-purple-light px-1.5 py-0.5 rounded text-xs font-mono">
          {children}
        </code>
      );
    },
    pre: ({ children }) => (
      <pre className="bg-bg-tertiary rounded-lg p-4 overflow-x-auto mb-4 border border-border-subtle text-sm">
        {children}
      </pre>
    ),
    blockquote: ({ children }) => (
      <blockquote className="border-l-2 border-text-muted pl-4 my-3 text-text-muted italic">
        {children}
      </blockquote>
    ),
    a: ({ href, children }) => {
      // Handle .md links as internal navigation
      if (onNavigate && href && (href.endsWith('.md') || /\.md[#?]/.test(href))) {
        const resolvedPath = resolveRelativePath(href, currentFilePath);
        return (
          <button
            onClick={() => onNavigate(resolvedPath)}
            className="text-accent-purple-light hover:underline cursor-pointer inline"
          >
            {children}
          </button>
        );
      }
      return (
        <a href={href} className="text-accent-purple-light hover:underline" target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      );
    },
    hr: () => <hr className="border-border-subtle my-6" />,
    strong: ({ children }) => (
      <strong className="font-semibold text-text-primary">{children}</strong>
    ),
    table: ({ children }) => (
      <div className="overflow-x-auto mb-4">
        <table className="text-sm text-text-secondary border-collapse border border-border-subtle w-full">
          {children}
        </table>
      </div>
    ),
    th: ({ children }) => (
      <th className="border border-border-subtle px-3 py-1.5 text-left font-semibold text-text-primary bg-bg-tertiary text-xs">
        {children}
      </th>
    ),
    td: ({ children }) => (
      <td className="border border-border-subtle px-3 py-1.5 text-xs">{children}</td>
    ),
  };
}

/**
 * Resolve a relative .md link against the current file's directory.
 */
function resolveRelativePath(href: string, currentFilePath?: string): string {
  // Strip hash/query
  const cleanHref = href.split('#')[0].split('?')[0];

  // If already absolute VPS path, return as-is
  if (cleanHref.startsWith('/opt/') || cleanHref.startsWith('/var/')) {
    return cleanHref;
  }

  // If no current file context, prepend the UT root
  if (!currentFilePath) {
    const relative = cleanHref.replace(/^\.\//, '');
    return `/opt/union-threads/${relative}`;
  }

  // Resolve relative to current file's directory
  const currentDir = currentFilePath.substring(0, currentFilePath.lastIndexOf('/'));

  if (cleanHref.startsWith('./')) {
    return `${currentDir}/${cleanHref.slice(2)}`;
  }
  if (cleanHref.startsWith('../')) {
    let dir = currentDir;
    let remaining = cleanHref;
    while (remaining.startsWith('../')) {
      dir = dir.substring(0, dir.lastIndexOf('/'));
      remaining = remaining.slice(3);
    }
    return `${dir}/${remaining}`;
  }

  // No prefix — treat as relative to current dir
  return `${currentDir}/${cleanHref}`;
}
